import { z } from "zod/v3";
import { AgentConfig } from "@ag-ui/client";
import { LanggraphAgent } from "@cloudbase/agent-adapter-langgraph";
import { createAgent, createMiddleware, DynamicTool } from "langchain";
import { AIMessage } from "@langchain/core/messages";
import { END } from "@langchain/langgraph";

export class LangchainAgent extends LanggraphAgent {
  constructor(
    config: AgentConfig & {
      agent: ReturnType<typeof createAgent>;
    }
  ) {
    super({
      ...config,
      compiledWorkflow: config.agent,
      adapterName: "LangChain",
    } as any);
  }
}

export function clientTools() {
  // @ts-ignore - createMiddleware generics can hit TS2589 during DTS build in some TS/dependency versions
  return createMiddleware({
    name: "ClientTools",
    stateSchema: z.object({
      client: z.object({
        tools: z.array(
          z.object({
            name: z.string(),
            description: z.string(),
            schema: z.any(),
          })
        ),
      }),
    }),

    // Add frontend tools to LLM's tool list
    wrapModelCall: (request, handler) => {
      const clientToolsList = (
        (request.state.client?.tools || []) as DynamicTool[]
      ).map((tool) => {
        const { name, description, schema } = tool;
        return { name, description, schema };
      });

      return handler({
        ...request,
        tools: [...(request.tools || []), ...clientToolsList],
      });
    },

    // After model: check if all tool calls are frontend actions
    // If so, skip ToolNode by jumping to END
    // Note: #createAfterModelRouter does direct comparison (jumpTo === END),
    // NOT using parseJumpToTarget(), so we must use the actual END constant.
    // Using "as any" to bypass TypeScript type mismatch (bug in langchain types).
    afterModel: {
      canJumpTo: [END as any],
      hook: (state) => {
        const messages = state.messages || [];
        const lastMessage = messages.at(-1);

        // Check if last message is AIMessage with tool calls
        if (!AIMessage.isInstance(lastMessage)) return;
        if (!lastMessage.tool_calls || lastMessage.tool_calls.length === 0)
          return;

        // Get frontend tool names
        const clientToolNames = new Set(
          ((state.client?.tools || []) as DynamicTool[]).map((t) => t.name)
        );

        // If no frontend tools defined, let it go to ToolNode normally
        if (clientToolNames.size === 0) return;

        // Check if ANY tool call is a frontend action
        const anyClientTool = lastMessage.tool_calls.some((call) =>
          clientToolNames.has(call.name)
        );

        if (anyClientTool) {
          // Has frontend action, skip ToolNode and let frontend handle it
          return { jumpTo: END as any };
        }

        // All are server-side tools, let ToolNode handle them
        return;
      },
    },
  });
}
